/**
 * @file ProofPhotos.tsx
 * @description Collapsible photo gallery for order proof-of-pack and product verification photos.
 *
 * Renders thumbnails in a grid. Clicking opens a full-size lightbox dialog.
 * Uses the Mission Control HUD theme.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Collapse,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VerifiedIcon from '@mui/icons-material/Verified';
import InventoryIcon from '@mui/icons-material/Inventory';
import config from '../../../config';
import { getValidToken } from '../../../services/tokenRefreshService';
import { useMissionTheme, type MissionColorTokens } from '../theme';

/** Photo record shape from the API */
interface OrderPhoto {
  id: string;
  orderId: string;
  orderLineId: string | null;
  photoType: 'product_verification' | 'proof_of_pack';
  downloadUrl: string;
  thumbnailUrl: string;
  createdAt: string;
}

interface ProofPhotosProps {
  orderId: string;
}

/** Fetch photos from admin API */
async function fetchPhotos(orderId: string): Promise<OrderPhoto[]> {
  const token = await getValidToken();
  const res = await fetch(`${config.API_URL}/admin/orders/${orderId}/photos`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data || [];
}

export default function ProofPhotos({ orderId }: ProofPhotosProps) {
  const { mc, glass } = useMissionTheme();
  const [photos, setPhotos] = useState<OrderPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await fetchPhotos(orderId);
    setPhotos(data);
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const proofPhotos = photos.filter((p) => p.photoType === 'proof_of_pack');
  const verificationPhotos = photos.filter((p) => p.photoType === 'product_verification');

  // Don't render if no photos and not loading
  if (!loading && photos.length === 0) return null;

  return (
    <Box sx={{ ...glass, p: 2 }}>
      {/* Header */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CameraAltIcon sx={{ color: mc.secondary, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700} color={mc.textPrimary}>
            Picking Photos
          </Typography>
          {!loading && (
            <Chip
              label={photos.length}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: `${mc.secondary}20`,
                color: mc.secondary,
              }}
            />
          )}
        </Box>
        <IconButton size="small" sx={{ color: mc.textSecondary }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={20} sx={{ color: mc.secondary }} />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {/* Proof of Pack */}
            {proofPhotos.length > 0 && (
              <PhotoSection
                label="Proof of Pack"
                icon={<InventoryIcon sx={{ fontSize: 14 }} />}
                photos={proofPhotos}
                color={mc.success}
                mc={mc}
                onPhotoClick={setLightboxUrl}
              />
            )}

            {/* Product Verification */}
            {verificationPhotos.length > 0 && (
              <PhotoSection
                label="Product Verification"
                icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                photos={verificationPhotos}
                color={mc.info}
                mc={mc}
                onPhotoClick={setLightboxUrl}
              />
            )}
          </Box>
        )}
      </Collapse>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxUrl !== null}
        onClose={() => setLightboxUrl(null)}
        maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setLightboxUrl(null)}
            sx={{
              position: 'absolute',
              top: -40,
              right: 0,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt="Order photo"
              style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

/** Grouped photo section (Proof of Pack / Product Verification) */
function PhotoSection({
  label,
  icon,
  photos,
  color,
  mc,
  onPhotoClick,
}: {
  label: string;
  icon: React.ReactNode;
  photos: OrderPhoto[];
  color: string;
  mc: MissionColorTokens;
  onPhotoClick: (url: string) => void;
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="caption" fontWeight={600} color={color}>
          {label}
        </Typography>
        <Typography variant="caption" color={mc.textMuted}>
          ({photos.length})
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {photos.map((photo) => (
          <Box
            key={photo.id}
            onClick={() => onPhotoClick(photo.downloadUrl)}
            sx={{
              width: 80,
              height: 80,
              borderRadius: 1.5,
              overflow: 'hidden',
              cursor: 'pointer',
              border: `2px solid ${mc.glassBorder}`,
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: color,
                boxShadow: `0 0 8px ${color}40`,
                transform: 'scale(1.05)',
              },
            }}
          >
            <img
              src={photo.thumbnailUrl || photo.downloadUrl}
              alt={photo.photoType}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
